import React from 'react';
import { InputAdornment } from '@mui/material';
import { FormikProps, isNaN } from 'formik';
import { useTranslation } from 'react-i18next';
import { TextField } from '../../../../form/textfield/textfield';
import { Grid } from '../../../../grid/grid';
import { useRootStore } from '../../../../../../providers/root-store-provider';
import { FairPriceFormValues } from '../../../form-values/fair-price-form-values-interface';
import { cn } from '../../../../../../utils/tailwind';

interface FairPriceTabFcfParametersProps {
    formikProps: FormikProps<FairPriceFormValues>;
    showResult: boolean;
}

export const FairPriceTabFcfParameters: React.FC<FairPriceTabFcfParametersProps> = ({ formikProps, showResult }) => {
    const { t } = useTranslation();
    const {
        symbolsStore: { symbolFairPriceStore },
    } = useRootStore();
    const { values, setFieldValue, errors } = formikProps;
    const showError = (field: keyof FairPriceFormValues) => !!errors[field] && !!values[field];

    return (
        <div
            className={cn(
                'rounded-medium border border-border px-small pt-medium',
                showResult ? 'mb-[-3rem] pb-[calc(theme(spacing.small)+theme(spacing.medium))]' : 'mb-vsmall pb-small',
            )}
        >
            <div className="mb-medium text-h3 font-bold leading-h3 text-brand-secondary">
                {t('layout.dialogs.fairPrice.fcf.paramsLabel')}
            </div>
            <Grid cols={{ zero: 1, sm: 3 }} gap={2} marginBottom={'small'}>
                <TextField
                    label={t('layout.dialogs.fairPrice.fcf.fcfPerShareInput')}
                    value={values.fcfPerShare}
                    fullWidth
                    error={showError('fcfPerShare')}
                    helperText={showError('fcfPerShare') ? errors.fcfPerShare : undefined}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <span className="text-text-muted">$</span>
                            </InputAdornment>
                        ),
                    }}
                    onChange={(e) => {
                        const raw = e.target.value;
                        setFieldValue('fcfPerShare', raw);
                        const val = Number(raw.replace(',', '.'));
                        if (!isNaN(val)) symbolFairPriceStore.setInputFcfPerShare(val);
                    }}
                />
                <TextField
                    label={t('layout.dialogs.fairPrice.fcf.fcfGrowth')}
                    value={values.fcfGrowthRate}
                    fullWidth
                    error={showError('fcfGrowthRate')}
                    helperText={showError('fcfGrowthRate') ? errors.fcfGrowthRate : undefined}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <span className="text-text-muted">%</span>
                            </InputAdornment>
                        ),
                    }}
                    onChange={(e) => {
                        const raw = e.target.value;
                        setFieldValue('fcfGrowthRate', raw);
                        const val = Number(raw.replace(',', '.'));
                        if (!isNaN(val) && val > 0) symbolFairPriceStore.setInputGrowthRateFCF(val);
                    }}
                />
                <TextField
                    label={t('layout.dialogs.fairPrice.fcf.targetYield')}
                    value={values.fcfTargetYield}
                    fullWidth
                    error={showError('fcfTargetYield')}
                    helperText={showError('fcfTargetYield') ? errors.fcfTargetYield : undefined}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <span className="text-text-muted">%</span>
                            </InputAdornment>
                        ),
                    }}
                    onChange={(e) => {
                        const raw = e.target.value;
                        setFieldValue('fcfTargetYield', raw);
                        const val = Number(raw.replace(',', '.'));
                        if (!isNaN(val) && val > 0) symbolFairPriceStore.setInputTargetFCFYield(val);
                    }}
                />
            </Grid>
            <TextField
                label={t('layout.dialogs.fairPrice.result.requiredReturn')}
                value={values.fcfRequiredReturn}
                fullWidth
                error={showError('fcfRequiredReturn')}
                helperText={showError('fcfRequiredReturn') ? errors.fcfRequiredReturn : undefined}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <span className="text-text-muted">%</span>
                        </InputAdornment>
                    ),
                }}
                onChange={(e) => {
                    const raw = e.target.value;
                    setFieldValue('fcfRequiredReturn', raw);
                    const val = Number(raw.replace(',', '.'));
                    if (!isNaN(val) && val > 0) symbolFairPriceStore.setInputFcfRequiredReturn(val);
                }}
            />
        </div>
    );
};
